import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { NetworkPermissionService } from '../core/network-permission.service';
import { CriticalErrorNavigationService } from '../error/critical-error-navigation.service';
import { RequestErrorPolicyService } from '../error/request-error-policy.service';
import { AuthTokenStorageService } from '../init/auth-token-storage.service';
import { ToastService } from '../shared/toast/toast.service';
import { AuthAlertService } from './auth-alert.service';

@Injectable()
export class GlobalInterceptor implements HttpInterceptor {
  private readonly skipAuthHeaderPaths = [
    '/cipa/authentication/token',
    '/cipa/authentication/token/refresh',
    '/auth/login',
    '/auth/refresh',
  ];

  constructor(
    private readonly toastService: ToastService,
    private readonly authAlertService: AuthAlertService,
    private readonly authTokenStorage: AuthTokenStorageService,
    private readonly networkPermissionService: NetworkPermissionService,
    private readonly criticalErrorNavigationService: CriticalErrorNavigationService,
    private readonly requestErrorPolicy: RequestErrorPolicyService,
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const request = this.applyAuthHeader(this.applyContentTypeHeader(req));

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => this.handleHttpError(error, request)),
    );
  }

  private applyContentTypeHeader(req: HttpRequest<any>): HttpRequest<any> {
    if (req.headers.get('Content-Type') === null && this.shouldAttachJsonContentType(req)) {
      return req.clone({ headers: req.headers.set('Content-Type', 'application/json') });
    }

    if (req.headers.get('Content-Type') === 'null') {
      return req.clone({ headers: req.headers.delete('Content-Type') });
    }

    return req.clone();
  }

  private applyAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    const token = this.authTokenStorage.getAccessToken();

    if (token !== '' && !this.shouldSkipAuthHeader(req)) {
      return req.clone({ headers: req.headers.set('X-SMOCipa-Authorization', `Bearer ${token}`) });
    }

    return req;
  }

  private shouldSkipAuthHeader(req: HttpRequest<any>): boolean {
    const url = req.url.toLowerCase();
    return this.skipAuthHeaderPaths.some((path) => url.includes(path));
  }

  private shouldAttachJsonContentType(req: HttpRequest<any>): boolean {
    const method = req.method.toUpperCase();
    const supportsBody = method === 'POST' || method === 'PUT' || method === 'PATCH';
    return supportsBody && req.body !== null && req.body !== undefined;
  }

  private handleHttpError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    console.log(`Interceptor - Error ${error.status}`, error);
    (error as any)._handled = true;

    if (error.error instanceof Blob) {
      return from(this.parseBlobError(error)).pipe(
        switchMap((parsedError: HttpErrorResponse) => this.routeError(parsedError, request)),
      );
    }

    return this.routeError(error, request);
  }

  private routeError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    if (this.isNetworkError(error)) {
      return this.handleNetworkError(error, request);
    }

    return this.handleManagedHttpError(error, request);
  }

  private async parseBlobError(error: HttpErrorResponse): Promise<HttpErrorResponse> {
    try {
      const blob = error.error as Blob;
      const text = await blob.text();
      const json = JSON.parse(text);
      (error as any).error = json;
      return error;
    } catch (parseError) {
      console.error('Failed to parse blob error:', parseError);
      return error;
    }
  }

  private isAuthError(error: HttpErrorResponse): boolean {
    return error.status === 401 || error.status === 403;
  }

  private isNetworkError(error: HttpErrorResponse): boolean {
    return error.status === 0 || !error.status || error.status === 504;
  }

  private handleAuthError(error: HttpErrorResponse): Observable<never> {
    this.authAlertService.showNoPermissionAction();
    return throwError(() => error);
  }

  private handleNetworkError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    return from(this.networkPermissionService.isLocalNetworkAccessError(request.url, error.status)).pipe(
      switchMap((isLocalNetworkAccessError: boolean) => {
        if (isLocalNetworkAccessError) {
          return this.handleLocalNetworkAccessError(error, request);
        }

        return this.handleManagedHttpError(error, request);
      }),
    );
  }

  private handleLocalNetworkAccessError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    if (this.isSMOLinkRequest(request)) {
      this.toastService.showError(
        'O SMOLink não está respondendo. Verifique se o aplicativo SMOLink está em execução ao lado do relógio do sistema e se foi permitido acesso à rede local nas configurações do navegador.',
      );
      return throwError(() => error);
    }

    if (this.isSMOAssinadorRequest(request)) {
      this.toastService.showError(
        'O SMOAssinador não está respondendo. Verifique se o aplicativo SMOAssinador está em execução ao lado do relógio do sistema e se foi permitido acesso à rede local nas configurações do navegador.',
      );
      return throwError(() => error);
    }

    this.toastService.showError(
      'Acesso à rede local negado. Por favor, permita o acesso à rede local nas configurações do navegador.',
    );

    return throwError(() => error);
  }

  private handleManagedHttpError(error: HttpErrorResponse, request: HttpRequest<any>): Observable<never> {
    if (error.status === 400 && this.isSMOLinkRequest(request)) {
      return EMPTY;
    }

    const handling = this.requestErrorPolicy.classifyHttpError(error);

    if (handling.shouldShowToast && handling.message) {
      this.toastService.error(handling.message);
    }

    if (handling.shouldRedirect) {
      const backendMessage: string | undefined = error.error?.message ?? undefined;
      void this.criticalErrorNavigationService.navigateToHttpError(error, undefined, backendMessage);
    }

    return throwError(() => error);
  }

  private isSMOLinkRequest(request: HttpRequest<any>): boolean {
    const url = request.url.toLowerCase();
    const isLocalhost = url.includes('localhost') || url.includes('127.0.0.1') || url.includes('::1');
    const hasSMOLink = url.includes('smolink') || url.includes(':45675');
    return isLocalhost && hasSMOLink;
  }

  private isSMOAssinadorRequest(request: HttpRequest<any>): boolean {
    const url = request.url.toLowerCase();
    const isLocalhost = url.includes('localhost') || url.includes('127.0.0.1') || url.includes('::1');
    const hasSMOAssinador = url.includes('smoassinador') || url.includes(':45684');
    return isLocalhost && hasSMOAssinador;
  }
}
